import { Database } from '@nozbe/watermelondb';

import { ShoppingListServiceOptions } from '../../../../types/dbService';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { InventoryProductModel } from '../../../../types/InventoryProduct';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { ProductType } from '../../../../types/Products';
import {
    ShoppingListModel,
    ShoppingListProductSize,
} from '../../../../types/ShoppingList';
import { ShoppingListProductModel } from '../../../../types/ShoppingListProduct';
import { getNowISO } from '../../../../utils/date';
import InventoryChange from '../../InventoryChange';
import InventoryProduct from '../../InventoryProduct';
import Product from '../../Product';

export const prepareAddShoppingListToInventory = async ({
    shoppingListElement,
    shoppingListProducts,
    addedToInventoryTime,
    userId,
    organisationID,
    database,
    serviceOptions,
}: {
    shoppingListElement: ShoppingListModel;
    shoppingListProducts: ShoppingListProductModel[];
    addedToInventoryTime: string;
    userId: string;
    organisationID: string;
    database: Database;
    serviceOptions: ShoppingListServiceOptions;
}): Promise<{
    shoppingListToUpdate: ShoppingListModel;
    inventoryProductsToAdd: InventoryProductModel[];
    inventoryChanges: InventoryChangeModel[];
}> => {
    const sharedServiceOptions = {
        database: serviceOptions.database,
        imageService: serviceOptions.imageService,
        logger: serviceOptions.logger,
        validation: serviceOptions.validation,
    };
    const legacySharedServiceOptions = {
        database: sharedServiceOptions.database,
        imageService: sharedServiceOptions.imageService,
        logDBAction: sharedServiceOptions.logger.logDBAction,
        validation: sharedServiceOptions.validation,
    };
    const inventoryProductService = new InventoryProduct(
        legacySharedServiceOptions,
    );
    const inventoryChangesService = new InventoryChange(
        legacySharedServiceOptions,
    );
    const productService = new Product(sharedServiceOptions);
    const inventoryProductsCollection =
        database.collections.get<InventoryProductModel>('inventory_products');

    const inventoryProductsToAdd: InventoryProductModel[] = [];
    const inventoryChanges: InventoryChangeModel[] = [];
    const shoppingListToUpdate = shoppingListElement.prepareUpdate(
        (shoppingList) => {
            shoppingList.addedToInventoryTime = addedToInventoryTime;
        },
    );

    await Promise.all(
        shoppingListProducts.map(async (shoppLstProd) => {
            const productSize = await productService.getByID(
                shoppLstProd.productId,
            );

            if (productSize.productType === ProductType.custom) {
                return;
            }

            const inventoryProduct = await inventoryProductService.getByParam(
                'product_id',
                shoppLstProd.productId,
            );

            if (inventoryProduct.length === 1) {
                inventoryChanges.push(
                    await prepareInventoryChange({
                        inventoryProductId: inventoryProduct[0].id,
                        quantityChange: shoppLstProd.totalQuantity,
                        userId,
                        inventoryChangesService,
                    }),
                );
                return;
            }

            const inventoryProductToAdd =
                inventoryProductsCollection.prepareCreate((inventoryProd) => {
                    inventoryProd.productId = shoppLstProd.productId;
                    inventoryProd.userId = userId;
                    inventoryProd.organisationId = organisationID;
                    inventoryProd.quantity = 0;
                    inventoryProd.favourite = false;
                });

            inventoryProductsToAdd.push(inventoryProductToAdd);
            inventoryChanges.push(
                await prepareInventoryChange({
                    inventoryProductId: inventoryProductToAdd.id,
                    quantityChange: shoppLstProd.totalQuantity,
                    userId,
                    inventoryChangesService,
                }),
            );
        }),
    );

    return {
        shoppingListToUpdate,
        inventoryProductsToAdd,
        inventoryChanges,
    };
};

const prepareInventoryChange = ({
    inventoryProductId,
    quantityChange,
    userId,
    inventoryChangesService,
}: {
    inventoryProductId: string;
    quantityChange: ShoppingListProductSize['quantity'];
    userId: string;
    inventoryChangesService: InventoryChange;
}) =>
    inventoryChangesService.prepareAdd(
        {
            inventoryProductId,
            quantityChange,
            processAt: new Date(getNowISO()),
        },
        userId,
    );

export const logAddToInventoryActions = ({
    inventoryProductsToAdd,
    inventoryChanges,
    logDBAction,
    tableName,
}: {
    inventoryProductsToAdd: InventoryProductModel[];
    inventoryChanges: InventoryChangeModel[];
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    if (inventoryProductsToAdd.length > 0) {
        logDBAction({
            message: 'Add products to inventory - create inventory products',
            modelName: tableName,
            payload: inventoryProductsToAdd,
        });
    }

    if (inventoryChanges.length > 0) {
        logDBAction({
            message: 'Add products to inventory - create inventory changes',
            modelName: tableName,
            payload: inventoryChanges,
        });
    }
};
